import CarotekColors from "@carotek/utils/Colors";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { AttributeValueModel } from "@insite/client-framework/Types/ApiModels";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    maximumNumberAttributeTypes: number;
    extendedStyles?: ProductAttributesStyles;
}

type Props = OwnProps & HasProduct;

export interface ProductAttributesStyles {
    container?: GridContainerProps;
    attributeTypeGridItem?: GridItemProps;
    attributeValueGridItem?: GridItemProps;
    attributeTypeText?: TypographyPresentationProps;
    attributeValueText?: TypographyPresentationProps;
    wrapper?: InjectableCss;
}

export const productAttributesStyles: ProductAttributesStyles = {
    container: {
        gap: 0,
        css: css`
            [class^="TypographyStyle"] {
                font-size: 12px;
                color: ${CarotekColors.grey13};
            }
        `,
    },
    attributeTypeGridItem: {
        css: css`
            overflow: hidden;
            flex-basis: auto;
            width: auto;
            max-width: 100%;
        `,
    },
    attributeValueGridItem: {
        css: css`
            overflow: hidden;
            flex-basis: auto;
            width: auto;
            max-width: 100%;
        `,
    },
    attributeTypeText: {
        weight: 600,
        css: css`
            margin-right: 3px;
        `,
    },
    wrapper: {
        css: css`
            width: 100%;
            display: flex;
        `,
    },
};

const ProductAttributes: FC<Props> = ({ product: { attributeTypes }, maximumNumberAttributeTypes, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(productAttributesStyles, extendedStyles));

    if (!attributeTypes || attributeTypes.length === 0) {
        return null;
    }

    const maximum = Math.min(maximumNumberAttributeTypes, 100);
    const limitedAttributeTypes = attributeTypes.slice(0, maximum);

    const displayAttributeValues = (attributeValues: AttributeValueModel[] | null) => {
        return (
            <Typography {...styles.attributeValueText}>
                {(attributeValues ?? []).map(a => a.valueDisplay).join(", ")}
            </Typography>
        );
    };

    return (
        <GridContainer {...styles.container}>
            {limitedAttributeTypes.map(attributeType => (
                <StyledWrapper key={attributeType.id.toString()} {...styles.wrapper}>
                    {attributeType.name !== "Product_Detail" ? (
                        <GridItem {...styles.attributeTypeGridItem}>
                            <Typography
                                {...styles.attributeTypeText}
                                data-test-selector={`attributeType${attributeType.id}`}
                            >
                                {attributeType.label || attributeType.name}:
                            </Typography>
                        </GridItem>
                    ) : null}

                    <GridItem
                        {...styles.attributeValueGridItem}
                        data-test-selector={`attributeValuesFor${attributeType.id}`}
                    >
                        {displayAttributeValues(attributeType.attributeValues)}
                    </GridItem>
                </StyledWrapper>
            ))}
        </GridContainer>
    );
};

export default withProduct(ProductAttributes);
